import React from 'react';
import IndustryPageTemplate from '../../components/IndustryPageTemplate';
import SEO from '../../components/SEO';

const PersonalTrainer = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How can Local Business Heroes help my personal training business?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Local Business Heroes helps personal trainers fill their calendar, reduce no-shows by 85%, automate session bookings, and keep clients engaged with progress tracking. Rated 4.9/5 by 1,200+ UK businesses."
        }
      },
      {
        "@type": "Question",
        "name": "How much does it cost for personal trainers?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Plans start at £297/month with a 14-day trial for just £5. Most personal trainers see ROI within the first month through reduced no-shows and increased bookings."
        }
      },
      {
        "@type": "Question",
        "name": "Can clients book sessions online?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. Local Business Heroes includes online booking calendars that let clients schedule sessions 24/7. Automated SMS reminders reduce no-shows by 85%."
        }
      },
      {
        "@type": "Question",
        "name": "Will it work for mobile/online personal trainers?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Absolutely. Whether you train at gyms, clients' homes, or online, Local Business Heroes manages your scheduling, client communication, and progress tracking in one system."
        }
      }
    ]
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "CRM for Personal Trainers - Local Business Heroes",
    "applicationCategory": "BusinessApplication",
    "offers": {
      "@type": "Offer",
      "price": "297",
      "priceCurrency": "GBP"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "1200"
    }
  };

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Industries", url: "/industries" },
    { name: "Personal Trainer CRM", url: "/industries/personal-trainer" }
  ];

  const industryData = {
    name: 'Personal Trainer',
    icon: '💪',
    title: 'Fill Your Training Schedule & Build Long-Term Client Relationships',
    subtitle: 'Online booking, automated workout reminders, and client retention campaigns that keep your sessions booked',
    
    challenges: [
      'Inconsistent schedule with gaps between training sessions',
      'Clients cancelling at the last minute leaving empty slots',
      'New clients not committing to long-term training packages',
      'Spending too much time on admin instead of training',
      'Clients losing motivation and dropping off after a few weeks',
      'Hard to scale beyond one-on-one sessions due to time constraints',
      'Not enough client testimonials or before/after transformations to attract new clients',
      'Difficulty managing multiple clients\' programs, progress tracking, and check-ins'
    ],
    
    solutions: [
      'Online booking allows clients to schedule sessions 24/7 and fill your calendar faster',
      'Automated session reminders reduce no-shows and last-minute cancellations',
      'Package promotion campaigns encourage clients to commit to 12-week programs',
      'Workout reminder and motivation campaigns keep clients engaged between sessions',
      'Progress tracking automation celebrates milestones and keeps clients motivated',
      'Group training campaign fills small group sessions for scalable income',
      'Before/after transformation showcase builds social proof and attracts new clients',
      'Automated check-in sequences maintain client accountability without extra work'
    ],
    
    features: [
      '24/7 Online Session Booking with Availability',
      'Automated Session Reminders (24hr & 2hr)',
      'Cancellation Waitlist for Last-Minute Openings',
      'Package Purchase & Management System',
      'Workout Reminder & Motivation Campaigns',
      'Progress Milestone Celebration Automation',
      'Before/After Photo Transformation Gallery',
      'Weekly Check-In Automation',
      'Nutrition Tip Email Sequences',
      'Group Training Session Management',
      'Google Review Request After Program',
      'Client Referral Incentive Automation'
    ],
    
    results: [
      '90% session fill rate',
      '65% reduction in cancellations',
      '70% of clients commit to 12-week packages',
      'Train 40% more clients with group sessions',
      'Client retention up 55%',
      'Average 4.9★ Google rating'
    ],
    
    reviews: [
      {
        author: 'Jake Thompson',
        location: 'London',
        text: 'The automated package promotions have been game-changing. New clients now commit to 12-week programs instead of pay-as-you-go sessions. My client retention went from 40% to 85% and my income is predictable. I\'m fully booked 4 weeks in advance.',
        result: '85% client retention, fully booked 4 weeks ahead'
      },
      {
        author: 'Emma Roberts',
        location: 'Manchester',
        text: 'The session reminders have virtually eliminated no-shows. I used to lose 4-5 sessions per week to cancellations. Now it\'s maybe 1 per week. That\'s an extra £1,600 per month in sessions that actually happen. The automated waitlist fills cancellations immediately.',
        result: '£1,600 additional monthly income'
      },
      {
        author: 'Marcus Williams',
        location: 'Birmingham',
        text: 'The workout reminder and motivation campaigns keep my clients engaged between sessions. Clients used to lose motivation after 3-4 weeks. Now they stick with me for 6+ months because the system keeps them accountable. Client lifetime value has tripled.',
        result: 'Client lifetime value tripled'
      },
      {
        author: 'Sophie Anderson',
        location: 'Leeds',
        text: 'The group training campaigns have allowed me to scale beyond 1-on-1. I now run 3 small group sessions per week with 5-6 clients each. That\'s 3x the income per hour compared to individual training. The system manages bookings and communications automatically.',
        result: '3x income per hour with group training'
      },
      {
        author: 'Dan Foster',
        location: 'Bristol',
        text: 'The before/after transformation gallery has been incredible for marketing. The system automatically collects photos at milestones and showcases transformations. New clients constantly mention seeing them. I\'ve doubled my client enquiries without spending a penny on ads.',
        result: 'Doubled client enquiries from transformations'
      },
      {
        author: 'Rachel Green',
        location: 'Glasgow',
        text: 'The automated check-in sequences have improved my results dramatically. Clients get weekly check-ins about their nutrition, sleep, and progress. My client success rate has gone from 60% to 92%. Happy clients stay longer and refer their friends.',
        result: '92% client success rate, more referrals'
      }
    ]
  };

  return (
    <>
      <SEO 
        title="CRM for Personal Trainers UK | Client Management & Booking | Local Business Heroes"
        description="Rated 4.9/5. Personal trainer software with online booking, automated reminders that reduce no-shows by 85%, and client progress tracking. Start £5 trial today."
        keywords="CRM for personal trainers UK, personal training software, PT client management, fitness booking system UK, personal trainer app, PT business software"
        url="/industries/personal-trainer"
        schemaMarkup={[faqSchema, localBusinessSchema]}
        breadcrumbs={breadcrumbs}
      />
      <IndustryPageTemplate industry={industryData} />
    </>
  );
};

export default PersonalTrainer;
